/**
 * Issuer link repair (2026-09-29) — issuers with no Cynapse link park their
 * director trades and holder filings as `no_issuer`. Two repairs, each a
 * durable job, dry run first:
 *   rebind — move the link from an empty sibling of the same company
 *   split  — Cynapse takes the issuer's name off records wrongly carrying it
 *            and creates the issuer's own record (funds; listed companies
 *            since 2026-10-03 — their apply sends back the dry run's list)
 */
import { apiClient } from './config.js';
import { waitForMaintenanceJob, type MaintenanceJobStatus } from './tools.js';

export interface IssuerLinkSibling {
  entity_id: string;
  canonical_identifier: string | null;
  display_name: string;
  entity_type: string;
  cynapse_entity_id: string;
  match: 'identifier' | 'name';
  announcements: number;
}

export interface IssuerLinkCandidate {
  entity_id: string;
  canonical_identifier: string | null;
  display_name: string;
  entity_type: string;
  cynapse_sync_status: string | null;
  parked: number;
  announcements: number;
  siblings: IssuerLinkSibling[];
  can_split: boolean;
  /** Why a splittable type still can't split: a legacy CXA row keyed on the
   *  TMX issuer code, or a code that is not a ticker. */
  split_block?: string | null;
}

export type IssuerLinkRepair =
  | { kind: 'rebind'; entity_id: string; from_entity_id: string; detach_mentions?: string[] }
  /** Move the link only — the old owner keeps its rows and is left unlinked. */
  | { kind: 'transfer'; entity_id: string; from_entity_id: string }
  | { kind: 'split'; entity_id: string; remove_from?: string[] };

/** The Assayer row that owns a Cynapse record, keyed by that record's id. */
export interface IssuerLinkOwner {
  entity_id: string;
  canonical_identifier: string | null;
  display_name: string;
  entity_type: string;
  /** split dry runs: the owner's name matches the issuer's (a merge is right). */
  same_name?: boolean;
}

export interface IssuerLinkRepairOutcome {
  kind: 'rebind' | 'split' | 'transfer';
  /** transfer: what stays on the old owner (it keeps its rows). */
  from_keeps?: Record<string, number>;
  /** rebind dry run (G9): exact per-table counts from the real merge, rolled back. */
  would_move?: Record<string, number>;
  /** rebind dry run: the merge would fail (e.g. a unique clash) — the reason. */
  would_fail?: string;
  /** rebind with a replaced link: rows reset to resolve again against the new record. */
  reset_against_old_record?: {
    old_cynapse_entity_id: string;
    director_trades: { trade_id: string; announcement_id: string; old_person_entity_id: string | null; old_status: string }[];
    holder_filings: { filing_id: string; announcement_id: string; old_status: string }[];
    cynapse_edges_not_ended: string;
  };
  /** rebind dry run: the sibling's mentions by the name the filing used. */
  sibling_mentions?: { company_name: string; mention_ids: string[] }[];
  /** rebind: mentions unlinked before the merge, with their old values. */
  detach?: { mention_id: string; company_name: string; old_entity_id: string; old_cynapse_entity_id: string | null; old_resolution_status: string }[];
  /** split: 'company' (listed company) or 'fund'. */
  path?: 'company' | 'fund';
  /** split, ASX-side rows: is the row really listed? (trading-markets-expert's check) */
  listing_evidence?: {
    has_evidence: boolean; reasons: string[]; needs_review: boolean;
    /** on_list_name_mismatch: the name the code is listed under today, and filings likely bound to the wrong company. */
    exchange_name?: string; probably_misbound_filings?: number;
  };
  owners?: Record<string, IssuerLinkOwner>;
  applied: boolean;
  error?: string;
  entity?: { display_name: string; canonical_identifier: string | null; entity_type: string };
  from?: { display_name: string; canonical_identifier: string | null; entity_type: string; cynapse_entity_id: string | null };
  sibling_references?: Record<string, number>;
  unmapped?: Record<string, number>;
  moved?: Record<string, number>;
  cynapse?: {
    removals?: Record<string, { canonical_name: string; aliases_removed: string[]; entity_aliases_removed: { alias_text: string; alias_kind: string }[] }>;
    would_create?: { name: string; entity_type: string; ticker: string; exchange_code: string | null };
    entity_id?: string | null;
    was_created?: boolean | null;
    canonical_name?: string;
    error?: string;
    /** company path: the records found carrying the name — sent back on apply. */
    remove_from?: string[];
    /** refusals: alias_holder_is_this_company / ticker_held_by_another_company. */
    holders?: { entity_id: string; canonical_name?: string; entity_type?: string }[];
    /** records holding the ticker under another name — reported, never reused. */
    other_ticker_holders?: { entity_id: string; canonical_name?: string; entity_type?: string }[];
  };
  cynapse_entity_id?: string;
  resolved?: Record<string, number>;
}

export interface IssuerLinkRepairResult {
  dry_run: boolean;
  repairs: number;
  applied: number;
  results: IssuerLinkRepairOutcome[];
}

export async function getIssuerLinkCandidates(includeUnparked = false): Promise<IssuerLinkCandidate[]> {
  const { data } = await apiClient.get<{ candidates: IssuerLinkCandidate[] }>(
    '/tools/issuer-link-repair/candidates',
    { params: { include_unparked: includeUnparked } },
  );
  return data.candidates;
}

/** Submits the repairs as one durable job and waits for it. Only one repair
 *  job runs at a time; if one is already live the server returns it, which
 *  this reports as an error rather than showing that job's results. */
export async function runIssuerLinkRepair(
  repairs: IssuerLinkRepair[],
  dryRun: boolean,
  onProgress?: (s: MaintenanceJobStatus) => void,
): Promise<IssuerLinkRepairResult> {
  const { data } = await apiClient.post<{ job_id: string; note?: string }>(
    '/tools/issuer-link-repair',
    { repairs },
    { params: { dry_run: dryRun } },
  );
  if (data.note) throw new Error('Another issuer link repair is still running — try again when it finishes.');
  return waitForMaintenanceJob<IssuerLinkRepairResult>(data.job_id, onProgress, 2000);
}
